import type { JSX } from 'solid-js'
import { createMemo, createSignal, For, Show } from 'solid-js'
import { ChevronLeft, ChevronRight } from 'lucide-solid'

const DOW = ['L', 'M', 'M', 'G', 'V', 'S', 'D']

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1) }
function daysInMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate() }
function sameDay(a: Date, b: Date) { return a.toDateString() === b.toDateString() }

export const DatePicker = (props: {
  value?: Date
  onChange: (d: Date) => void
  minDate?: Date
  maxDate?: Date
  isDateDisabled?: (d: Date) => boolean
}): JSX.Element => {
  const [view, setView] = createSignal(startOfMonth(props.value ?? new Date()))
  const grid = createMemo(() => {
    const start = view()
    const total = daysInMonth(start)
    const firstDow = (start.getDay() + 6) % 7  // Mon=0
    const cells: (Date | null)[] = Array(firstDow).fill(null)
    for (let i = 1; i <= total; i++) cells.push(new Date(start.getFullYear(), start.getMonth(), i))
    while (cells.length % 7) cells.push(null)
    return cells
  })
  const monthLabel = () => view().toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
  return (
    <div class="inline-block bg-surface border border-border rounded-lg p-3 min-w-[280px]">
      <div class="flex items-center justify-between mb-3">
        <button type="button" class="p-1 rounded hover:bg-surface-alt" onClick={() => setView(new Date(view().getFullYear(), view().getMonth() - 1, 1))} aria-label="Mese precedente"><ChevronLeft size={18} /></button>
        <span class="font-display font-semibold capitalize">{monthLabel()}</span>
        <button type="button" class="p-1 rounded hover:bg-surface-alt" onClick={() => setView(new Date(view().getFullYear(), view().getMonth() + 1, 1))} aria-label="Mese successivo"><ChevronRight size={18} /></button>
      </div>
      <div class="grid grid-cols-7 gap-1 text-xs text-muted mb-1">
        <For each={DOW}>{(d) => <div class="text-center">{d}</div>}</For>
      </div>
      <div class="grid grid-cols-7 gap-1">
        <For each={grid()}>
          {(d) => (
            <Show when={d} fallback={<div />}>
              {(dayAcc) => {
                // Reactive getters so Solid re-reads `props.value` after onChange.
                const isDisabled = () => {
                  const day = dayAcc()
                  if (props.minDate && day < props.minDate) return true
                  if (props.maxDate && day > props.maxDate) return true
                  return !!props.isDateDisabled?.(day)
                }
                const isSelected = () => !!props.value && sameDay(dayAcc(), props.value)
                return (
                  <button
                    type="button"
                    disabled={isDisabled()}
                    data-selected={isSelected() ? 'true' : 'false'}
                    onClick={() => props.onChange(dayAcc())}
                    class="h-9 text-sm rounded-md hover:bg-accent-soft disabled:opacity-30 disabled:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-white data-[selected=true]:font-semibold data-[selected=true]:ring-2 data-[selected=true]:ring-accent data-[selected=true]:ring-offset-1 data-[selected=true]:shadow-md"
                  >
                    {dayAcc().getDate()}
                  </button>
                )
              }}
            </Show>
          )}
        </For>
      </div>
    </div>
  )
}
